import React, {
    useContext,
    useState,
    useEffect,
    useMemo,
} from 'react';
import { useMutation, useQueries, useQuery } from '@tanstack/react-query';
import { Link } from "react-router";

import { getBasketData, refetchBasketData, getCustomer } from "../queries/global";
import { NotificationContext, handleError } from '../context/NotificationContext';

import config from "../config/config";
import * as QUERIES from "../queries/query-functions";
import * as UTILS from "../utils/utils";

import DonationPrompt from './donation-prompt';
import MembershipPrompt from './membership-prompt';
import BasketEvent from './basket-event';
import AdditionalDonation from './additional-donation';
import BasketSummary from '../components/basket-summary';
import PromoCodeInput from '../components/promo-code-input';
import LoadingIndicator from '../components/loading-indicator';


const basket_summary_message = config.OPTION_FIELDS.basket_summary_message;

export default function Basket() {

    const {notification, setNotification} = useContext(NotificationContext);

    const [hasDonationUpsell, setHasDonationUpsell] = useState(false);
    const [page, setPage] = useState("basket");
    const [excludedDonations, setExcludedDonations] = useState([]);


    const { data: basketData, isPending: basketDataPending } = getBasketData();
    const { data: customer, isPending: customerPending } = getCustomer();

    console.log("Basket data:", basketData);

    const planIds = useMemo(() => {
        if (!basketData?.tickets) return [];

        return [
            ...new Set(basketData.tickets.map(ticket => ticket.planId)),
        ];
    }, [basketData]);

    const planQueries = useQueries({
        queries: planIds.map(id => ({
            queryKey: ["planData", id],
            queryFn: () => QUERIES.getPlanData(id),
            staleTime: 86400000,
        })),
    });

    const plansMap = useMemo(() => {
        const map = {};

        planQueries.forEach(q => {
            if (q.data) {                
                map[q.data.plan_id] = q.data;
            }
        });

        return map;
    }, [planQueries]);

    const groupedTickets = useMemo(() => {
        if (!basketData?.tickets) return [];
        const grouped_tickets = UTILS.groupTicketsByInstance(basketData.tickets);

        const grouped = grouped_tickets.reduce((acc, ticket) => {

            const day = ticket.instance_date_utc.split("T")[0];

            if (!acc[day]) {
                acc[day] = { instance: [], supplementary: [] };
            }

            if (ticket.seats[0].seat 
                || (plansMap[ticket.tickets[0]?.planId]?.type === 'Unreserved' && ticket.tickets[0].event.attribute_FirstTierArtform !== 'None')
                // || (plansMap[ticket.tickets[0]?.planId]?.type === 'Unreserved' && plansMap[ticket.tickets[0]?.planId]?.name !== 'General Admission' )
                // || (plansMap[ticket.tickets[0]?.planId]?.type === 'Unreserved' && !!ticket.tickets[0].event.duration)) 
            ){
                acc[day].instance.push(ticket);
            } else {
                acc[day].supplementary.push(ticket);
            }

            return acc;
        }, {});

        return Object.entries(grouped).map(([day, data]) => ({
            day,
            ...data
        }));
    }, [basketData, plansMap]);

    useEffect(() => {
        if (!basketData || !basketData.tickets) return;

        const has_upsell = UTILS.ticketsHaveDonationUpsell(basketData.tickets);
        setHasDonationUpsell(has_upsell);

        const excluded_donations = UTILS.getExcludedDonations(basketData);
        setExcludedDonations(excluded_donations);

    }, [basketData]);
        
    const removeDonation = useMutation({
        mutationFn: (donationId) => QUERIES.removeDonation(donationId),
        onError: (error) => {
            handleError(error.message, setNotification);
        },
        onSuccess: () => {
            refetchBasketData();
        },
    });
        
    const removeMembership = useMutation({
        mutationFn: (membershipId) => QUERIES.removeMembership(membershipId),
        onError: (error) => {
            handleError(error.message, setNotification);
        },
        onSuccess: () => {
            refetchBasketData();
        },
    });
        
    const removeGiftVoucher = useMutation({
        mutationFn: (giftVoucherId) => QUERIES.removeGiftVoucher(giftVoucherId),
        onError: (error) => {
            handleError(error.message, setNotification);
        },
        onSuccess: () => {
            refetchBasketData();
        },
    });

    const patchMembership = useMutation({
        mutationFn: ({subscriptionId, body}) => QUERIES.patchMembership(subscriptionId, body),
        onError: (error) => {
            handleError(error.message, setNotification);
        },
        onSuccess: (data) => {
            refetchBasketData();
        },
    });

    useEffect(() => {
        refetchBasketData();
    }, [])

    const getMonthsDiffBetweenDates = (dateInitial, dateFinal) =>
        Math.max(
            (dateFinal.getFullYear() - dateInitial.getFullYear()) * 12 +
            dateFinal.getMonth() -
            dateInitial.getMonth(),
            0
        );
    
    //if the basket data is undefined or pending, show a loading indicator and load it
    
    if (basketDataPending || customerPending) return <LoadingIndicator type="page" />;

    return (
        <div className="booking-pathway__container booking-pathway__basket">
            <div className='booking-pathway__main-content'>
                {page == "basket" ?
                    <>
                        <h1 className="booking-pathway__header"><span>Basket</span></h1>

                        { !basketDataPending && (!groupedTickets || !groupedTickets.length) && !basketData?.membershipSubscriptions.length && !basketData?.donations.length && !basketData?.giftVouchers.length &&
                            <p>There are no items in your basket.</p>
                        }

                        <div className='basket-items'>
                            {groupedTickets && 
                                <>
                                    <h2 className='basket-item__groupe-title'>Tickets</h2>                            
                                    {groupedTickets.map(group => (
                                        <div className='basket-item__groupes' key={group.day}>
                                            { group.instance.length > 0 && (
                                                <div className='basket-item__groupe'>
                                                    <BasketEvent key={group.instance[0].event_id} event={group.instance[0]} />
                                                </div>
                                            )}

                                            { group.supplementary.length > 0 && (
                                                <div className='basket-item__groupe basket-item__groupe--extra'>
                                                    <BasketEvent event={group.supplementary} isSupplementaryView />
                                                </div>
                                            )}
                                        </div>
                                    ))}                                
                                </>
                            }
                            

                            { basketData?.membershipSubscriptions.length ? 
                                <div className='basket-item__groupe basket-item__groupe--membership'>
                                    <h2 className='basket-item__groupe-title'>Membership</h2>
                                    {basketData.membershipSubscriptions.map((membership) => (

                                    <div key={membership.id} className='basket-item__ticket-list--item'>
                                        <p className='basket-item__ticket-list--area'>{membership.membership.name}, {getMonthsDiffBetweenDates(new Date(membership.startDate), new Date(membership.expiryDate))} months</p>
                                        {/* <p className='basket-item__ticket-list--type'>
                                             <span className="form-field--checkbox">
                                                        <input type="checkbox" 
                                                                id={`membershipautorenew-${membership.id}`}
                                                                value={membership.id}
                                                                defaultChecked={membership.autoRenew}
                                                                disabled={patchMembership.isPending}
                                                                onChange={(e) =>
                                                                    patchMembership.mutateAsync({
                                                                        subscriptionId: membership.id,
                                                                        body: [{ autoRenew: e.target.checked }]
                                                                    })
                                                                } />
                                                        <label htmlFor={`membershipautorenew-${membership.id}`}>Automatically renew and store card details</label>
                                                    </span>
                                        </p> */}

                                        <p className='basket-item__ticket-list--price'>
                                            <span>£{membership.membership.price.toFixed(2)}</span>
                                        </p>
                                        <div className='basket-item__ticket-list--amend-ticket'>
                                            <button type="button" 
                                                        onClick={() => removeMembership.mutateAsync(membership.id)} 
                                                        className='btn btn--remove'>
                                                    Delete
                                                </button> 
                                        </div>                                           
                                    </div>
                                ))}</div>
                                : null
                            }

                            { basketData.donations.length > 0 ?
                                <div className='basket-item__groupe basket-item__groupe--donation'>
                                    <h2 className='basket-item__groupe-title'>Donation</h2>
                                {basketData.donations.map(donation => {
                                    if (excludedDonations.includes(donation.id)) return null;

                                    return (
                                    <div key={donation.id} className='basket-item__ticket-list--item'>
                                        <p className='basket-item__ticket-list--area'>Donation</p>
                                        <p className='basket-item__ticket-list--price'>
                                            £{donation.amount.toFixed(2)}
                                        </p>
                                        <div className='basket-item__ticket-list--amend-ticket'>
                                            <button
                                                type="button"
                                                onClick={() => removeDonation.mutateAsync(donation.id)}
                                                className='btn btn--remove'
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </div>
                                    );
                                })}
                                </div>
                                : null
                            }

                            { basketData.giftVouchers.length ? 
                                <div  className='basket-item__groupe basket-item__groupe--gift-voucher'>
                                    <h2 className='basket-item__groupe-title'>Gift voucher</h2>

                                    { basketData.giftVouchers.map((voucher) => {
                                        const formattedDate = UTILS.formatDateTime(voucher.sendDate);

                                        return (

                                            <div key={voucher.id} className='basket-item__ticket-list--item'>
                                                <p className='basket-item__ticket-list--area basket-item__info--breakdown'>
                                                    {voucher.toName &&
                                                        <span><strong>To:</strong> {voucher.toName} {formattedDate ? `to be sent on ${formattedDate}` : ""}</span>
                                                    }
                                                    {voucher.fromName &&
                                                        <span><strong>From:</strong> {voucher.fromName}</span>
                                                    }
                                                    {voucher.deliveryEmailAddress &&
                                                        <span>{voucher.deliveryEmailAddress}</span>
                                                    }

                                                    
                                                </p>
                                                <p className='basket-item__ticket-list--price'>
                                                    £{voucher.amount.toFixed(2)}
                                                </p>
                                                <div className='basket-item__ticket-list--amend-ticket'>
                                                    <button type="button" 
                                                            onClick={() => removeGiftVoucher.mutateAsync(voucher.id)} 
                                                            className='btn btn--remove'>
                                                        Delete
                                                    </button>    
                                                </div>
                                            </div>
                                        )})
                                    }
                                </div>
                                : null
                            }
                        </div>
                        { !basketData?.membershipSubscriptions.length ?
                            <MembershipPrompt hasSubscriptions = {!!customer?.subscriptions?.length} />
                            : null
                        }
                        
                    </>
                :
                page == "additional-donation" ?
                    <AdditionalDonation />
                :null
                }
                
            </div>

            <div className='booking-pathway__summary'>

                <MembershipPrompt isSummary hasSubscriptions = {!!customer?.subscriptions?.length} />

                <h2 className='sr-only'>Basket summary</h2>
                <BasketSummary />

                { basket_summary_message && 
                <div class="booking-pathway__advisory-message"
                    dangerouslySetInnerHTML={{
                                __html:basket_summary_message.innerHTML,
                            }}
                ></div>
            }
            </div>
            
            
            <div className='booking-pathway__footer'>
                {!basketData ?
                    <p className='btn btn--primary disabled'>Checkout</p>
                : 
                page == "basket" && hasDonationUpsell ?
                    <button type="button" role="link" className='btn btn--primary' onClick={() => { setPage("additional-donation"); window.scrollTo({ top: 0, behavior: 'smooth' }) }}>Continue</button>
                :
                    <Link to="/checkout" className='btn btn--primary'>Checkout - £{basketData.total.toFixed(2)}</Link>
                }
                {/* <a href="/whats-on" className='btn'>Continue browsing</a> */}
            </div>
        </div>
    );
}